//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { EditorState, Modifier, SelectionState } from 'draft-js';

import { ISmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import {
  getCharBeforeSelection,
  getCurrentContentBlock,
  getCurrentStateProperties,
} from '@components/molecules/smart-input/smartInputUtils';
import { SocialTextEntityType } from '@utils/social-text-utils/socialTextUtilsModel';
import { isDefined } from '@utils/filterPredicates';

/**
 * Updates the current mention query with the full mention
 *
 * @param mention The mention string to be added
 * @param smartInputContext
 * @param position
 */
export const insertMentionAtPosition = (
  mention: string,
  smartInputContext: ISmartInputContext,
  position: {
    blockKey: string;
    start: number;
    end: number;
  },
) => {
  const { blockKey, end, start } = position;
  const editorState = smartInputContext.editor.getEditorState();

  const { currentContentState } = getCurrentStateProperties(editorState);
  const selection = SelectionState.createEmpty(blockKey).merge({
    anchorOffset: start,
    focusOffset: end,
  });

  smartInputContext.editor.setEditorState(
    EditorState.forceSelection(editorState, selection),
  );

  const replacedContent = Modifier.replaceText(
    currentContentState,
    selection,
    mention,
  );

  const newEditorState = EditorState.push(
    editorState,
    replacedContent,
    'insert-fragment',
  );

  smartInputContext.editor.setEditorState(
    EditorState.forceSelection(
      newEditorState,
      replacedContent.getSelectionAfter(),
    ),
    true,
  );

  return Promise.resolve();
};

/**
 * Returns the blockKey and cursor index for a given editor state
 *
 * @param editorState
 */
export const getCurrentCursorPosition = (editorState: EditorState) => {
  const { currentContentState, currentSelectionState } =
    getCurrentStateProperties(editorState);
  const currentBlock = getCurrentContentBlock(
    currentContentState,
    currentSelectionState,
  );
  const currentBlockKey = currentBlock.getKey();
  const cursorIndex = currentSelectionState.getStartOffset();

  return {
    currentBlockKey,
    cursorIndex,
  };
};

/**
 * Returns whether the cursor is currently within a mention
 *
 * @param editorState
 * @param smartInputContext
 */
export const getIsCursorInMention = (
  editorState: EditorState,
  smartInputContext: ISmartInputContext,
) => {
  const { currentContentState, currentSelectionState } =
    getCurrentStateProperties(editorState);
  const currentBlock = getCurrentContentBlock(
    currentContentState,
    currentSelectionState,
  );

  const currentBlockText = currentBlock.getText();
  const cursorIndex = currentSelectionState.getStartOffset();

  const mentionsWithinBlock = smartInputContext
    .socialTextParseMethod(currentBlockText)
    .filter((entity) => {
      return entity.type === SocialTextEntityType.Mention;
    });

  const isCursorInMention = mentionsWithinBlock.some((mention) => {
    if (isDefined(mention.start) && isDefined(mention.end)) {
      return cursorIndex >= mention.start && cursorIndex <= mention.end;
    }

    return false;
  });

  return isCursorInMention;
};

/**
 * Returns whether the character before the cursor is an @ symbol
 *
 * @param editorState
 */
export const getIsCursorAfterAtSymbol = (editorState: EditorState) => {
  const { currentContentState, currentSelectionState } =
    getCurrentStateProperties(editorState);

  const charBeforeCursor = getCharBeforeSelection(
    currentContentState,
    currentSelectionState,
  );

  return charBeforeCursor === '@';
};
